import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  LOCATION_STATUSES,
  LOCATION_TYPES,
  buildLocationTree,
  entityId,
  locationLabel,
  type LocationNode,
  type LocationStatus,
  type LocationTreeNode,
  type LocationType,
} from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { deleteLocation, listLocations, saveLocation } from '../api/locations';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { Layout } from '../components/Layout';
import { LocationPlansSection } from '../components/LocationPlansSection';

const TYPE_LABELS: Record<string, string> = {
  site: 'Standort',
  building: 'Gebäude',
  floor: 'Etage',
  area: 'Bereich',
  room: 'Raum',
  section: 'Abschnitt',
  outdoor: 'Außenbereich',
};

/**
 * German display labels for `LocationStatus`, shared by the tree row badge
 * (see `LocationTree`) and the status select - one convention for the same
 * values everywhere on this page, not one per place they are shown.
 */
const STATUS_LABELS: Record<LocationStatus, string> = {
  active: 'Aktiv',
  planned: 'Geplant',
  inactive: 'Inaktiv',
};

function LocationTree({
  entries,
  depth = 0,
  onSelect,
}: {
  entries: readonly LocationTreeNode[];
  depth?: number;
  onSelect: (node: LocationTreeNode['node']) => void;
}): ReactNode {
  return (
    <ul className="location-tree" role={depth === 0 ? 'tree' : 'group'}>
      {entries.map((entry) => (
        <li
          key={entry.node.id}
          role="treeitem"
          {...(entry.children.length > 0 ? { 'aria-expanded': true } : {})}
        >
          <div className="location-tree__row" style={{ paddingInlineStart: `${depth * 20}px` }}>
            <button
              type="button"
              className="location-tree__name"
              onClick={() => onSelect(entry.node)}
            >
              {locationLabel(entry.node)}
            </button>
            <span className="badge">
              {TYPE_LABELS[entry.node.locationType] ?? entry.node.locationType}
            </span>
            {entry.node.status !== 'active' ? (
              <span className="badge">{STATUS_LABELS[entry.node.status]}</span>
            ) : null}
            {entry.orphaned ? (
              // Shown, not hidden: a node whose parent is gone is a real
              // problem someone has to fix, and silently listing it at the top
              // would make the tree look correct when it is not.
              <span className="badge badge--warning">übergeordneter Ort fehlt</span>
            ) : null}
          </div>
          {entry.children.length > 0 ? (
            <LocationTree entries={entry.children} depth={depth + 1} onSelect={onSelect} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** One flattened row for the "parent" select, indented by tree depth. */
interface ParentOption {
  id: string;
  label: string;
  depth: number;
}

function flattenForParentOptions(
  entries: readonly LocationTreeNode[],
  excludeId: string | undefined,
  depth = 0,
): ParentOption[] {
  const options: ParentOption[] = [];
  for (const entry of entries) {
    if (entry.node.id === excludeId) {
      // Not just excluded itself: its whole subtree is skipped too, since the
      // API rejects reparenting a node under any of its own descendants. The
      // select would otherwise offer a choice guaranteed to fail and
      // indistinguishable from a valid one.
      continue;
    }
    options.push({ id: entry.node.id, label: locationLabel(entry.node), depth });
    options.push(...flattenForParentOptions(entry.children, excludeId, depth + 1));
  }
  return options;
}

/**
 * Location tree of one customer: site, building, floor, area and the other
 * location types.
 *
 * The tree itself (`LocationTree`) is read only decoration around
 * `buildLocationTree` - all editing happens through the form below it, which
 * doubles as create and edit form (same fields, whichever node is selected).
 * Selecting a node in the tree opens it for editing; the two toolbar buttons
 * open a blank form, either for a new root node or for a new child of the
 * currently selected node.
 */
export function LocationsPage(): ReactNode {
  const { session } = useSession();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);

  const [formMode, setFormMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [selectedNode, setSelectedNode] = useState<LocationNode | undefined>(undefined);

  const [locationType, setLocationType] = useState<LocationType>('site');
  const [parentLocationId, setParentLocationId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState(0);
  const [status, setStatus] = useState<LocationStatus>('active');

  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback((id: string, cancelledRef: { current: boolean }) => {
    Promise.all([getCustomer(id), listLocations(id)])
      .then(([loadedCustomer, locationResponse]) => {
        if (cancelledRef.current) return;
        setCustomerName(loadedCustomer.name);
        setLocations(locationResponse.locations);
      })
      .catch((caught: unknown) => {
        if (cancelledRef.current) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Orte konnten nicht geladen werden.',
        );
      });
  }, []);

  useEffect(() => {
    if (!customerIdParam) {
      return;
    }
    const cancelledRef = { current: false };
    loadAll(customerIdParam, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [customerIdParam, loadAll]);

  if (session?.status !== 'authenticated') {
    return null;
  }
  const { me } = session;
  const canWrite = me.permissions.includes('customers:write');

  if (!customerIdParam) {
    return null;
  }
  // Narrowed once, so every nested handler below sees a definite `string`
  // instead of repeating a non-null assertion at each call site.
  const customerId = customerIdParam;

  function resetForm(defaults: { locationType: LocationType; parentLocationId: string }): void {
    setLocationType(defaults.locationType);
    setParentLocationId(defaults.parentLocationId);
    setCode('');
    setName('');
    setDescription('');
    setOrder(0);
    setStatus('active');
    setSaveError(undefined);
  }

  function openCreateRoot(): void {
    setSelectedNode(undefined);
    setFormMode('create');
    resetForm({ locationType: 'site', parentLocationId: '' });
  }

  function openCreateChild(): void {
    if (!selectedNode) return;
    setFormMode('create');
    resetForm({ locationType: 'site', parentLocationId: selectedNode.id });
  }

  function handleSelect(node: LocationNode): void {
    setSelectedNode(node);
    setFormMode('edit');
    setLocationType(node.locationType);
    setParentLocationId(node.parentLocationId ?? '');
    setCode(node.code ?? '');
    setName(node.name ?? '');
    setDescription(node.description ?? '');
    setOrder(node.order);
    setStatus(node.status);
    setSaveError(undefined);
    setDeleteError(undefined);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveError(undefined);
    setSaving(true);
    try {
      const targetId =
        formMode === 'edit' && selectedNode
          ? selectedNode.id
          : entityId.location(crypto.randomUUID());
      const saved = await saveLocation(customerId, targetId, {
        locationType,
        ...(parentLocationId ? { parentLocationId } : {}),
        code,
        name,
        description,
        order,
        status,
        ...(formMode === 'edit' && selectedNode?._etag ? { ifMatchEtag: selectedNode._etag } : {}),
      });
      setSelectedNode(saved);
      setFormMode('edit');
      const cancelledRef = { current: false };
      loadAll(customerId, cancelledRef);
    } catch (caught: unknown) {
      setSaveError(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!selectedNode) return;
    setDeleteError(undefined);
    setDeleting(true);
    try {
      await deleteLocation(customerId, selectedNode.id);
      setFormMode('closed');
      setSelectedNode(undefined);
      const cancelledRef = { current: false };
      loadAll(customerId, cancelledRef);
    } catch (caught: unknown) {
      setDeleteError(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  }

  const tree = locations ? buildLocationTree(locations) : [];
  const parentOptions = flattenForParentOptions(
    tree,
    formMode === 'edit' ? selectedNode?.id : undefined,
  );

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        <div className="card">
          <h1>Orte</h1>
          {canWrite ? (
            <p>
              <button type="button" className="button button--primary" onClick={openCreateRoot}>
                Ort anlegen
              </button>{' '}
              {selectedNode ? (
                <button type="button" className="button button--ghost" onClick={openCreateChild}>
                  Untergeordneten Ort anlegen
                </button>
              ) : null}
            </p>
          ) : null}

          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : locations === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Orte werden geladen ...
            </p>
          ) : locations.length === 0 ? (
            <p className="muted">Noch kein Ort angelegt.</p>
          ) : (
            <LocationTree entries={tree} onSelect={handleSelect} />
          )}
        </div>

        {formMode !== 'closed' ? (
          <div className="card">
            <h2>{formMode === 'edit' ? 'Ort bearbeiten' : 'Ort anlegen'}</h2>
            <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
              <div className="form-field">
                <label htmlFor="location-type">Ortsart</label>
                <select
                  id="location-type"
                  disabled={!canWrite}
                  value={locationType}
                  onChange={(event) => setLocationType(event.target.value as LocationType)}
                >
                  {LOCATION_TYPES.map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {TYPE_LABELS[candidate] ?? candidate}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="location-code">Kurzbezeichnung</label>
                <input
                  id="location-code"
                  type="text"
                  disabled={!canWrite}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="location-name">Name</label>
                <input
                  id="location-name"
                  type="text"
                  disabled={!canWrite}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="location-description">Beschreibung</label>
                <textarea
                  id="location-description"
                  rows={3}
                  disabled={!canWrite}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>

              <div className="form-field">
                <label htmlFor="location-order">Sortierung</label>
                <input
                  id="location-order"
                  type="number"
                  disabled={!canWrite}
                  value={order}
                  onChange={(event) => setOrder(Number(event.target.value))}
                />
              </div>

              <div className="form-field">
                <label htmlFor="location-status">Status</label>
                <select
                  id="location-status"
                  disabled={!canWrite}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as LocationStatus)}
                >
                  {LOCATION_STATUSES.map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {STATUS_LABELS[candidate]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="location-parent">Übergeordneter Ort</label>
                <select
                  id="location-parent"
                  disabled={!canWrite}
                  value={parentLocationId}
                  onChange={(event) => setParentLocationId(event.target.value)}
                >
                  <option value="">(kein übergeordneter Ort)</option>
                  {parentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {'— '.repeat(option.depth)}
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {saveError ? <p className="notice notice--warning">{saveError}</p> : null}
              {deleteError ? <p className="notice notice--warning">{deleteError}</p> : null}

              {canWrite ? (
                <p>
                  <button className="button button--primary" type="submit" disabled={saving}>
                    {saving ? 'Speichert ...' : 'Speichern'}
                  </button>{' '}
                  {formMode === 'edit' ? (
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={deleting}
                      onClick={() => void handleDelete()}
                    >
                      {deleting ? 'Löscht ...' : 'Löschen'}
                    </button>
                  ) : null}
                </p>
              ) : null}
            </form>
          </div>
        ) : null}

        {formMode === 'edit' && selectedNode && me.features?.floorPlans === true ? (
          <LocationPlansSection
            customerId={customerId}
            location={selectedNode}
            locations={locations ?? []}
            canWrite={canWrite && me.permissions.includes('assets:write')}
          />
        ) : null}
      </section>
    </Layout>
  );
}
